import React, { useState } from 'react';
import { useI18n } from '../i18n/context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Camera, Loader2, User } from 'lucide-react';
import AvatarPickerModal from './AvatarPickerModal';
import { completeTelegramOnboarding } from '../services/api';
import { toast } from 'sonner';

/**
 * Без tsparticles/WebGL — в WebView Telegram часто глючит canvas; фон как на WelcomeScreen, но статичный.
 */
export default function TelegramOnboarding({ user, onCompleted }) {
  const { t } = useI18n();
  const [name, setName] = useState(() => (user?.name && String(user.name).trim()) || '');
  const [avatar, setAvatar] = useState(() => user?.avatar || '');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 3) {
      toast.error(t('tg.nameTooShort'));
      return;
    }
    setSaving(true);
    try {
      const { data: updated } = await completeTelegramOnboarding({
        name: trimmed,
        avatar: avatar && String(avatar).trim() ? String(avatar).trim() : null,
      });
      onCompleted?.(updated);
      toast.success(t('tg.done'));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || t('tg.saveFail');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] min-h-screen w-full max-w-[100vw] min-w-0 overflow-hidden bg-black">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.75)_70%,rgba(0,0,0,1)_100%)]" />

      <div className="relative z-10 flex min-h-[100dvh] min-h-screen flex-col items-center justify-center px-4 pb-10 pt-8">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-6 shadow-xl">
          <h1 className="text-center text-xl font-semibold text-foreground">{t('tg.nameTitle')}</h1>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            {t('tg.nameHint')}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="flex flex-col items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
                  title={t('tg.pickAvatar')}
                >
                  <Avatar className="h-24 w-24 border-2 border-border">
                    <AvatarImage src={avatar || undefined} alt="" />
                    <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                      {name?.trim()?.charAt(0)?.toUpperCase() || <User className="h-10 w-10" />}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-md">
                    <Camera className="h-4 w-4" />
                  </span>
                </button>
              </div>
              <p className="text-center text-sm text-muted-foreground">{t('tg.photoLabel')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tg-onboarding-name">{t('tg.nameLabel')}</Label>
              <Input
                id="tg-onboarding-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('tg.namePlaceholder')}
                autoComplete="name"
                className="text-base"
                maxLength={255}
              />
            </div>

            <Button type="submit" className="h-12 w-full text-base" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('tg.creating')}
                </>
              ) : (
                t('tg.create')
              )}
            </Button>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('mad:openTerms'));
              }}
              className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              {t('tos.linkLabel')}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('mad:openPrivacy'));
              }}
              className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              {t('privacy.linkLabel')}
            </button>
          </div>
        </div>
      </div>

      <AvatarPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={t('tg.avatarPickerTitle')}
        selectedUrl={avatar || undefined}
        onSelect={(url) => setAvatar(url)}
      />
    </div>
  );
}
